import { type FC, useState } from "react"

import { type CommunityAccess, type CommunityChannelProductAccess } from "@forento/core/models/community"
import { getCommunityAccessDisplayName } from "@forento/core/utilities/community"
import Tooltip from "@forento/ui/components/Tooltip"

import Button from "~/components/Button"
import { ArrowIcon, DeleteIcon, EditIcon } from "~/components/Icon"
import InputField from "~/components/InputField"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import Table from "~/components/Table"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import trpc, { query } from "~/utilities/trpc"

import CreateModal from "./CreateModal"
import EditModal from "./EditModal"

const searchableChannelCount = 8

const Channels: FC = () => {
	const alert = useAlert()
	const toast = useToast()

	const [search, setSearch] = useState("")
	const [isCreateModalOpen, setCreateModalOpen] = useState(false)
	const [editModal, setEditModal] = useState<{
		isOpen: boolean
		channel: {
			id: number | null
			name: string
			defaultAccess: CommunityAccess | null
			membershipAccess: { membershipId: number; access: CommunityAccess }[]
			productAccess: CommunityChannelProductAccess[]
		}
	}>({
		isOpen: false,
		channel: { id: null, name: "", defaultAccess: null, membershipAccess: [], productAccess: [] },
	})

	const { data, error, refetch } = query.community.listChannels.useQuery()
	const { mutate: moveChannelUp } = query.community.moveChannelUp.useMutation({
		async onSuccess() {
			await refetch()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to move channel. Please try again.")
		},
	})
	const { mutate: moveChannelDown } = query.community.moveChannelDown.useMutation({
		async onSuccess() {
			await refetch()
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to move channel. Please try again.")
		},
	})

	if (error) return <p>Failed to fetch channels.</p>

	if (data === undefined) return <PartialLoadingPage />

	async function handleDelete(id: number) {
		const channel = data?.find(item => item.id === id)
		if (channel === undefined) return

		const dialog = await alert.confirm(
			"Delete channel",
			`Are you sure you want to delete the channel **${channel.name}**? This will permanently **delete all posts in this channel**.`,
		)
		if (!dialog.result) return
		try {
			await trpc.community.deleteChannel.mutate(id)
			await refetch()
			dialog.close()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to create the channel. Please try again.")
		}
	}

	const isSearchable = data.length > searchableChannelCount
	const trimmedSearch = search.trim().toLowerCase()
	const isSearching = isSearchable && trimmedSearch.length > 0
	const rows = data
		.map((item, index) => ({ item, index }))
		.filter(({ item }) => !isSearching || item.name.toLowerCase().includes(trimmedSearch))

	return (
		<div className="flex flex-col items-end gap-4">
			<CreateModal isOpen={isCreateModalOpen} onClose={() => setCreateModalOpen(false)} onCreated={refetch} />
			<EditModal
				channel={editModal.channel}
				isOpen={editModal.isOpen}
				onClose={() => setEditModal(current => ({ ...current, isOpen: false }))}
				onSubmitted={refetch}
			/>
			{isSearchable && (
				<div className="flex w-full flex-wrap items-center justify-between gap-4">
					<p className="text-light text-sm font-semibold">
						{data.length} channels
						{isSearching && ` · ${rows.length} matching`}
					</p>
					<InputField
						value={search}
						onChange={setSearch}
						placeholder="Search channels"
						compact
						className="w-full max-w-72"
					/>
				</div>
			)}
			<Table
				isCompact
				columns={["Name", "Who has access", "Actions"]}
				emptyText="No channels match your search."
				data={rows.map(({ item, index }) => [
					<div className="flex flex-col gap-0.5">
						<span className="font-semibold">{item.name}</span>
						{item.id === null && <span className="text-light text-xs">Default channel</span>}
					</div>,
					<div className="flex flex-col gap-1">
						{item.defaultAccess ? (
							<AccessBadge access={item.defaultAccess} />
						) : (
							<span className="text-light">Everyone: no access</span>
						)}
						{item.membershipAccess.length + item.productAccess.length > 0 && (
							<span className="text-light text-xs">
								Plus{" "}
								{[
									item.membershipAccess.length > 0 &&
										pluralize(item.membershipAccess.length, "membership"),
									item.productAccess.length > 0 && pluralize(item.productAccess.length, "product"),
								]
									.filter(Boolean)
									.join(" and ")}
							</span>
						)}
					</div>,
					<div className="flex items-center gap-4">
						{item.id !== null && !isSearching ? (
							<>
								{index > 1 ? (
									<Tooltip tooltip="Move up">
										<Button className="size-6" onClick={() => moveChannelUp(item.id!)}>
											<ArrowIcon direction="up" className="size-full" />
										</Button>
									</Tooltip>
								) : (
									<div className="size-6" />
								)}
								{index < data.length - 1 ? (
									<Tooltip tooltip="Move down">
										<Button className="size-6" onClick={() => moveChannelDown(item.id!)}>
											<ArrowIcon direction="down" className="size-full" />
										</Button>
									</Tooltip>
								) : (
									<div className="size-6" />
								)}
							</>
						) : (
							<>
								<div className="size-6" />
								<div className="size-6" />
							</>
						)}
						<Tooltip tooltip="Edit">
							<Button className="size-6" onClick={() => setEditModal({ isOpen: true, channel: item })}>
								<EditIcon className="size-full" />
							</Button>
						</Tooltip>
						{item.id !== null && (
							<Tooltip tooltip="Delete">
								<Button className="size-6" onClick={() => handleDelete(item.id!)}>
									<DeleteIcon className="text-danger size-full" />
								</Button>
							</Tooltip>
						)}
					</div>,
				])}
			/>
			<Button variant="primary" onClick={() => setCreateModalOpen(true)}>
				Create new channel
			</Button>
		</div>
	)
}

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`

const AccessBadge: FC<{ access: CommunityAccess }> = ({ access }) => (
	<span className="bg-primary/10 text-primary self-start rounded-full px-2.5 py-1 text-xs font-semibold">
		Everyone: {getCommunityAccessDisplayName(access).toLowerCase()}
	</span>
)

export default Channels
